//#region imports
import React, { useState, useEffect, useMemo } from 'react';
import { Row, Col, Form } from 'react-bootstrap';
import Select from 'react-select';
import moment from 'moment';
import BeaconTable from './components/beacon-table';
import EmployeeLocationMap from './components/employee-location-map';
import GeoTable from './components/geo-table';
import FlightAuditFilters from './components/flight-audit-filters';
import { downloadCSV } from '_utils/csv-export';
import {
    getLocationsForPoint,
    compressGeoCoords,
    compressPointsByMinInterval,
    buildPolygonGeoJson,
    makeCircleGeoJson,
} from '_utils/geo-matching';
import { getLocationsForBeacon } from '_utils/beacon-matching';
import { useDispatch, useSelector } from 'react-redux';
import { selectIata } from '_redux/slices/main-screen/user';
import { getAirportLocation, selectAllAirportLocations } from '_redux/slices/timeline/timeline-areas';
import {
    fetchEmployeeAuditReport,
    selectEmployeeAuditReportData,
    selectEmployeeAuditReportStatus,
    selectEmployeeAuditReportError,
    selectEmployeeAuditReportCoordinates,
    selectEmployeeAuditBeaconData,
    selectEmployeeAuditBeaconStatus,
    selectEmployeeAuditBeaconError,
    selectEmployeeAuditBeaconCoordinates,
} from '_redux/slices/sla-reports/employee-audit-report';
//#endregion

//#region helpers
const toHHmm = (ts) =>
    new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
//#endregion

const EmployeeLocationAuditReport = ({ dateRange, runReportRef, onRunLoadingChange, onCanRunChange }) => {
    //#region redux
    const dispatch = useDispatch();
    const iata = useSelector(selectIata);
    const airportLocations = useSelector(selectAllAirportLocations);
    const reportData = useSelector(selectEmployeeAuditReportData);
    const reportCoordinates = useSelector(selectEmployeeAuditReportCoordinates);
    const reportStatus = useSelector(selectEmployeeAuditReportStatus);
    const reportError = useSelector(selectEmployeeAuditReportError);
    const beaconData = useSelector(selectEmployeeAuditBeaconData);
    const beaconCoordinates = useSelector(selectEmployeeAuditBeaconCoordinates);
    const beaconStatus = useSelector(selectEmployeeAuditBeaconStatus);
    const beaconError = useSelector(selectEmployeeAuditBeaconError);
    //#endregion

    //#region state
    const [startDate, setStartDate] = useState(moment().startOf('day').format('YYYY-MM-DDTHH:mm'));
    const [endDate, setEndDate] = useState(
        moment().endOf('day').format('YYYY-MM-DDTHH:mm')
    );
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [selectedForMap, setSelectedForMap] = useState(() => new Set());
    const [selectedBeaconsForMap, setSelectedBeaconsForMap] = useState(() => new Set());
    const [showBeaconIds, setShowBeaconIds] = useState(false);
    const [showCoordinates, setShowCoordinates] = useState(false);
    const [isCleaned, setIsCleaned] = useState(true);
    const [minIntervalMinutes, setMinIntervalMinutes] = useState(5);
    const [showGeoLine, setShowGeoLine] = useState(true);
    const [showBeaconLine, setShowBeaconLine] = useState(true);
    const [minBeaconIntervalMinutes, setMinBeaconIntervalMinutes] = useState(5);
    const [selectedLocationsForMap, setSelectedLocationsForMap] = useState([]);

    const isLoading = reportStatus === 'loading' || beaconStatus === 'loading';
    //#endregion

    //#region map selection handlers
    const getRowId = (row) => `geo-${row.latitude}-${row.longitude}-${row.timestamp}`;

    const handleToggleSelect = (rowKey) => {
        setSelectedForMap((prev) => {
            const next = new Set(prev);
            if (next.has(rowKey)) next.delete(rowKey);
            else next.add(rowKey);
            return next;
        });
    };

    const handleSelectAll = () => {
        if (geoCoords?.length) {
            setSelectedForMap(new Set(geoCoords.map((r) => getRowId(r))));
        }
    };

    const handleUnselectAll = () => {
        setSelectedForMap(new Set());
    };

    const getBeaconRowId = (row) =>
        row?.id ? `beacon-${row.id}` : `beacon-${row.major}-${row.minor}-${row.timestamp}`;

    const handleBeaconToggleSelect = (rowKey) => {
        setSelectedBeaconsForMap((prev) => {
            const next = new Set(prev);
            if (next.has(rowKey)) next.delete(rowKey);
            else next.add(rowKey);
            return next;
        });
    };

    const handleBeaconSelectAll = () => {
        if (mappableBeaconKeys?.size) {
            setSelectedBeaconsForMap(new Set(mappableBeaconKeys));
        }
    };

    const handleBeaconUnselectAll = () => {
        setSelectedBeaconsForMap(new Set());
    };
    //#endregion

    //#region export handlers
    const handleExportGeoCSV = () => {
        const locations = airportLocations || [];
        const enriched = (reportCoordinates || []).map((row) => ({
            ...row,
            areas: getLocationsForPoint(locations, row.latitude, row.longitude).join(', '),
        }));
        downloadCSV(enriched, `geolocation-records-${new Date().toISOString().slice(0, 10)}.csv`);
    };

    const handleExportBeaconCSV = () => {
        const locations = airportLocations || [];
        const enriched = (beaconCoordinates || []).map((row) => ({
            ...row,
            areas: getLocationsForBeacon(locations, row.major, row.minor).join(', '),
        }));
        downloadCSV(enriched, `beacon-events-${new Date().toISOString().slice(0, 10)}.csv`);
    };
    //#endregion

    //#region search handler
    const handleSearch = () => {
        if (!selectedEmployee?.employeeId || !iata) return;
        const params = {
            iata,
            employeeId: selectedEmployee.employeeId,
            startDate: new Date(startDate).toISOString(),
            endDate: new Date(endDate).toISOString(),
        };
        dispatch(fetchEmployeeAuditReport(params));
    };
    //#endregion

    //#region geo coords derived data
    const geoCoords = useMemo(() => {
        const raw = (reportCoordinates ?? [])
            .filter((p) => Number.isFinite(p.latitude) && Number.isFinite(p.longitude))
            .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        return isCleaned
            ? compressGeoCoords(raw, {
                  minMeters: 15,
                  dedupeIntervalSeconds: 30,
                  minIntervalMinutes: minIntervalMinutes || 0,
              })
            : raw;
    }, [reportCoordinates, isCleaned, minIntervalMinutes]);

    const selectedGeoCoords = useMemo(() => {
        return geoCoords.filter((p) => selectedForMap.has(getRowId(p)));
    }, [geoCoords, selectedForMap]);

    const pathGeoJson = useMemo(
        () => ({
            type: 'FeatureCollection',
            features:
                selectedGeoCoords.length >= 2
                    ? [
                          {
                              type: 'Feature',
                              geometry: {
                                  type: 'LineString',
                                  coordinates: selectedGeoCoords.map((p) => [p.longitude, p.latitude]),
                              },
                              properties: {},
                          },
                      ]
                    : [],
        }),
        [selectedGeoCoords]
    );
    //#endregion

    //#region beacon derived data
    const beaconLocations = useMemo(() => {
        const events = Array.isArray(beaconCoordinates) ? beaconCoordinates : [];
        const locations = Array.isArray(airportLocations) ? airportLocations : [];
        return events
            .map((evt) => {
                const match = locations.find(
                    (loc) =>
                        Array.isArray(loc.beaconDetails) &&
                        loc.beaconDetails.some(
                            (b) =>
                                Number(b.major) === Number(evt.major) &&
                                Number(b.minor) === Number(evt.minor)
                        )
                );
                if (!match) return null;
                return { ...match, isBeacon: true, beaconEvent: evt };
            })
            .filter(Boolean);
    }, [beaconCoordinates]);

    const beaconPointsAll = useMemo(() => {
        return (beaconLocations || [])
            .map((loc) => {
                if (!loc?.geofenceDetails?.type) return null;
                let lat = null;
                let lng = null;
                if (loc.geofenceDetails.type === 'circular') {
                    lat = Number(loc.geofenceDetails.circular?.lat);
                    lng = Number(loc.geofenceDetails.circular?.long);
                } else if (loc.geofenceDetails.type === 'polygon') {
                    lat = Number(loc.geofenceDetails.polygon?.[0]?.[0]);
                    lng = Number(loc.geofenceDetails.polygon?.[0]?.[1]);
                }
                if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
                const evt = loc.beaconEvent;
                const _rowKey = evt ? getBeaconRowId(evt) : null;
                return { lat, lng, ts: evt?.timestamp, _rowKey };
            })
            .filter(Boolean)
            .sort((a, b) => {
                const da = new Date(`1970-01-01T${a.ts}:00Z`).getTime();
                const db = new Date(`1970-01-01T${b.ts}:00Z`).getTime();
                return da - db;
            });
    }, [beaconLocations]);

    const mappableBeaconKeys = useMemo(
        () => new Set(beaconPointsAll.map((p) => p._rowKey).filter(Boolean)),
        [beaconPointsAll]
    );

    const beaconPointsFiltered = useMemo(() => {
        if (!selectedBeaconsForMap.size) return [];
        return beaconPointsAll.filter((p) => p._rowKey && selectedBeaconsForMap.has(p._rowKey));
    }, [beaconPointsAll, selectedBeaconsForMap]);

    const beaconPoints = useMemo(() => {
        return compressPointsByMinInterval(
            beaconPointsFiltered,
            minBeaconIntervalMinutes,
            (p) => p.ts
        );
    }, [beaconPointsFiltered, minBeaconIntervalMinutes]);

    const beaconPathGeoJson = useMemo(
        () => ({
            type: 'FeatureCollection',
            features:
                beaconPoints.length >= 2
                    ? [
                          {
                              type: 'Feature',
                              geometry: {
                                  type: 'LineString',
                                  coordinates: beaconPoints.map((p) => [p.lng, p.lat]),
                              },
                              properties: {},
                          },
                      ]
                    : [],
        }),
        [beaconPoints]
    );
    //#endregion

    //#region location geofences for map
    const locationOptions = useMemo(() => {
        const locs = airportLocations || [];
        return locs
            .filter((l) => l?.geofenceDetails?.type && l?.title)
            .map((l) => ({ value: l._id, label: l.title }));
    }, [airportLocations]);

    const geofencesToShow = useMemo(() => {
        if (!selectedLocationsForMap?.length || !airportLocations?.length) return [];
        const selectedIds = new Set(selectedLocationsForMap.map((s) => s.value));
        const fences = [];
        for (const loc of airportLocations) {
            if (!selectedIds.has(loc._id) || !loc?.geofenceDetails?.type) continue;
            const g = loc.geofenceDetails;
            if (g.type === 'polygon') {
                const pts = g.polygon || [];
                const fence = buildPolygonGeoJson(pts);
                if (fence && pts.length > 0) {
                    const [lat, lng] = pts[0];
                    fences.push({ fence, title: loc.title, anchor: [Number(lat), Number(lng)] });
                }
            } else if (g.type === 'circular') {
                const { lat, long, radius } = g.circular || {};
                if (lat != null && long != null) {
                    const fence = makeCircleGeoJson([lat, long], Number(radius) || 50);
                    fences.push({ fence, title: loc.title, anchor: [Number(lat), Number(long)] });
                }
            }
        }
        return fences;
    }, [selectedLocationsForMap, airportLocations]);
    //#endregion

    //#region effects
    useEffect(() => {
        if (iata) dispatch(getAirportLocation(iata));
    }, [dispatch, iata]);

    useEffect(() => {
        if (reportCoordinates?.length) {
            setIsCleaned(true);
            setMinIntervalMinutes(5);
        }
    }, [reportCoordinates]);

    useEffect(() => {
        if (reportCoordinates?.length && geoCoords?.length) {
            setSelectedForMap(new Set(geoCoords.map((r) => getRowId(r))));
        }
    }, [reportCoordinates]);

    useEffect(() => {
        if (onRunLoadingChange) onRunLoadingChange(isLoading);
    }, [isLoading, onRunLoadingChange]);

    useEffect(() => {
        if (beaconCoordinates?.length) {
            setSelectedBeaconsForMap(
                new Set(beaconCoordinates.map((r) => getBeaconRowId(r)))
            );
            setMinBeaconIntervalMinutes(5);
        }
    }, [beaconCoordinates]);
    //#endregion

    //#region render
    return (
        <div style={{ marginBottom: '600px' }}>
            <style>{`
                .employee-audit-geo-switch .form-check-input:checked {
                    background-color: #2563eb;
                    border-color: #2563eb;
                }
                .employee-audit-beacon-switch .form-check-input:checked {
                    background-color: #ea580c;
                    border-color: #ea580c;
                }
            `}</style>

            <FlightAuditFilters
                iata={iata}
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
                selectedFlight={null}
                setSelectedFlight={() => {}}
                selectedEmployee={selectedEmployee}
                setSelectedEmployee={setSelectedEmployee}
                onSearch={handleSearch}
                isLoading={isLoading}
                hideFlightDropdown
                runReportRef={runReportRef}
                onCanRunChange={onCanRunChange}
            />

            {(reportStatus === 'succeeded' || beaconStatus === 'succeeded') && (
            <div
                style={{
                    border: '1px solid #d1d5db',
                    borderRadius: 12,
                    padding: '20px 24px',
                    marginTop: 24,
                    backgroundColor: '#fafafa',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        marginBottom: 20,
                        paddingBottom: 12,
                        borderBottom: '1px solid #e5e7eb',
                    }}
                >
                    <i className="fas fa-file-alt" style={{ fontSize: 18, color: '#6b7280' }} />
                    <span style={{ fontSize: 16, fontWeight: 600, color: '#374151' }}>
                        Employee Location Report
                    </span>
                </div>

                {/* Location circles dropdown - above the map */}
                {reportStatus === 'succeeded' && (geoCoords.length > 0 || beaconPointsAll.length > 0) && locationOptions.length > 0 && (
                    <div
                        style={{
                            padding: '12px 0',
                            marginBottom: 16,
                            borderBottom: '1px solid #e5e7eb',
                            width: '100%',
                        }}
                    >
                        <label style={{ fontSize: 13, fontWeight: 500, color: '#6b7280', marginBottom: 8, display: 'block' }}>
                            Show location circles on map
                        </label>
                        <Select
                            isMulti
                            options={locationOptions}
                            value={selectedLocationsForMap}
                            onChange={setSelectedLocationsForMap}
                            placeholder="Select areas to highlight..."
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    minHeight: 36,
                                    fontSize: 14,
                                    fontFamily: 'inherit',
                                    maxWidth: 400,
                                }),
                                placeholder: (base) => ({
                                    ...base,
                                    fontSize: 14,
                                    fontFamily: 'inherit',
                                }),
                                multiValueLabel: (base) => ({
                                    ...base,
                                    fontSize: 13,
                                    fontFamily: 'inherit',
                                }),
                            }}
                        />
                    </div>
                )}

                <Row className="g-3" style={{ alignItems: 'flex-start' }}>
                    {reportStatus === 'succeeded' && (
                    <Col xs={12} md={6}>
                        {(geoCoords.length > 0 || beaconPointsAll.length > 0) ? (
                            <EmployeeLocationMap
                                selectedGeoCoords={selectedGeoCoords}
                                pathGeoJson={pathGeoJson}
                                beaconPathGeoJson={beaconPathGeoJson}
                                beaconPoints={beaconPoints}
                                showGeoLine={showGeoLine}
                                showBeaconLine={showBeaconLine}
                                geofencesToShow={geofencesToShow}
                                height={500}
                                title="Employee Path"
                                aboveMap={null}
                                footer={
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            alignItems: 'center',
                                            gap: 24,
                                            padding: '12px 16px',
                                            borderTop: '1px solid #e5e7eb',
                                            backgroundColor: '#f9fafb',
                                            fontSize: 14,
                                            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                            color: '#374151',
                                        }}
                                    >
                                        <Form.Check
                                            type="switch"
                                            id="employee-audit-show-geo-line"
                                            label={
                                                <span style={{ fontSize: 14, fontWeight: 500, padding: '2px 0', lineHeight: 1.25 }}>
                                                    Geo coordinates line
                                                </span>
                                            }
                                            checked={showGeoLine}
                                            onChange={() => setShowGeoLine((p) => !p)}
                                            style={{ marginBottom: 0 }}
                                            className="mb-0 employee-audit-geo-switch"
                                        />
                                        <Form.Check
                                            type="switch"
                                            id="employee-audit-show-beacon-line"
                                            label={
                                                <span style={{ fontSize: 14, fontWeight: 500, padding: '2px 0', lineHeight: 1.25 }}>
                                                    Beacon line
                                                </span>
                                            }
                                            checked={showBeaconLine}
                                            onChange={() => setShowBeaconLine((p) => !p)}
                                            style={{ marginBottom: 0 }}
                                            className="mb-0 employee-audit-beacon-switch"
                                        />
                                    </div>
                                }
                            />
                        ) : (
                            <div
                                style={{
                                    border: '1px solid #d1d5db',
                                    borderRadius: 12,
                                    overflow: 'hidden',
                                    backgroundColor: '#f9fafb',
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 10,
                                        padding: '12px 16px',
                                        borderBottom: '1px solid #e5e7eb',
                                        backgroundColor: '#f3f4f6',
                                    }}
                                >
                                    <i className="fas fa-map-marker-alt" style={{ fontSize: 16, color: '#9ca3af' }} />
                                    <span style={{ fontSize: 14, fontWeight: 600, color: '#6b7280' }}>Employee Path</span>
                                </div>
                                <div
                                    style={{
                                        height: 500,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: 24,
                                    }}
                                >
                                    <p style={{ margin: 0, fontSize: 14, color: '#6b7280' }}>No GPS locations</p>
                                    <p style={{ margin: '8px 0 0', fontSize: 13, color: '#9ca3af' }}>
                                        No geolocation data to display on the map.
                                    </p>
                                </div>
                            </div>
                        )}
                    </Col>
                    )}

                    <Col xs={12} md={6}>
                        {reportStatus === 'failed' && (
                            <p style={{ color: 'red', fontSize: 13 }}>Error: {reportError}</p>
                        )}
                        {reportStatus === 'succeeded' && (
                            <div style={{ marginBottom: 24 }}>
                                <GeoTable
                                    title={`Geolocation Records (${geoCoords.length})`}
                                    headers={['latitude', 'longitude', 'timestamp']}
                                    data={geoCoords.map((row) => ({
                                        ...row,
                                        timestamp: row.timestamp ? toHHmm(row.timestamp) : row.timestamp,
                                        _rowKey: getRowId(row),
                                    }))}
                                    locations={airportLocations || []}
                                    onExport={handleExportGeoCSV}
                                    selectedForMap={selectedForMap}
                                    onToggleSelect={handleToggleSelect}
                                    onSelectAll={handleSelectAll}
                                    onUnselectAll={handleUnselectAll}
                                    isCleaned={isCleaned}
                                    onCleanToggle={() => setIsCleaned((p) => !p)}
                                    minIntervalMinutes={minIntervalMinutes}
                                    onMinIntervalChange={setMinIntervalMinutes}
                                    showCoordinates={showCoordinates}
                                    onToggleShowCoordinates={() => setShowCoordinates((p) => !p)}
                                />
                            </div>
                        )}
                        {beaconStatus === 'failed' && (
                            <p style={{ color: 'red', fontSize: 13 }}>Beacon Error: {beaconError}</p>
                        )}
                        {beaconStatus === 'succeeded' && (
                            <BeaconTable
                                title={`Beacon Events (${(beaconCoordinates || []).length})`}
                                headers={['major', 'minor', 'timestamp']}
                                data={(beaconCoordinates || []).map((row) => ({
                                    ...row,
                                    _rowKey: getBeaconRowId(row),
                                }))}
                                locations={airportLocations || []}
                                onExport={handleExportBeaconCSV}
                                showBeaconIds={showBeaconIds}
                                onToggleShowBeaconIds={() => setShowBeaconIds((p) => !p)}
                                selectedForMap={selectedBeaconsForMap}
                                onToggleSelect={handleBeaconToggleSelect}
                                onSelectAll={handleBeaconSelectAll}
                                onUnselectAll={handleBeaconUnselectAll}
                                mappableBeaconKeys={mappableBeaconKeys}
                                minIntervalMinutes={minBeaconIntervalMinutes}
                                onMinIntervalChange={setMinBeaconIntervalMinutes}
                            />
                        )}
                    </Col>
                </Row>
            </div>
            )}
        </div>
    );
    //#endregion
};

export default EmployeeLocationAuditReport;
